import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text, Button, Card, Subtitle1, Switch, Badge } from '@fluentui/react-components';
import { CheckmarkCircleRegular, DismissRegular, ErrorCircleRegular, ClockRegular } from "@fluentui/react-icons";
import {
    MessageBar,
    MessageBarActions,
    MessageBarTitle,
    MessageBarBody,
    MessageBarGroup,
    MessageBarIntent,
    makeStyles,
    shorthands,
} from "@fluentui/react-components";

import { useSignInState } from '../signInState';
import useFetch from '../../Hooks/useFetchAPI';
import { FeatureSkeleton } from '../LoadingSkeletons';
import ModuleConfigTable from './ModuleConfigTable';
import { isValidGuid } from '../../Helpers/utils';
import AdminLink from './HelperComponents/AdminLink';

interface NotificationMessage {
    intent: MessageBarIntent;
    id: number;
    message: string;
};

const useStyles = makeStyles({
    messageBarGroup: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        ...shorthands.overflow("auto"),
    },
    enrollmentSection: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginTop: "16px",
        marginBottom: "16px",
        padding: "12px",
        backgroundColor: "var(--colorNeutralBackground2)",
        borderRadius: "4px",
    },
    enrollmentStatus: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
});


const ModuleSurfaceWarranty = ({ tenantConfigured }: { tenantConfigured: boolean }) => {
    const styles = useStyles();
    const [connectorTestError, setConnectorTestError] = useState<boolean>(false);
    const { signInState } = useSignInState();
    const featureName = 'IntuneReporting';
    const connectorName = 'SurfaceWarranty';
    const counterRef = useRef(0);

    const [featureConfigData, setFeatureConfigData] = useState<SurfaceWarrantyFeatureConfigData[] | null>(null);
    const [surfaceWarrantyConfigItems, setSurfaceWarrantyConfigItems] = useState<ModuleConfigTableProps[]>([]);

    const { fetchAPI: fetchConfig, apiData, isLoading, serverError } = useFetch({ scope: 'AdminUser' });
    const { fetchAPI: testConfig, apiData: testConfigData, isLoading: isLoadingTestConfig, serverError: serverErrorTestConfig } = useFetch({ scope: 'AdminUser' });
    const { fetchAPI: updateDatabase } = useFetch({ scope: 'AdminUser' });
    const { fetchAPI: enableDataSync, isLoading: isLoadingEnableDataSync } = useFetch({ scope: 'AdminUser' });
    const { fetchAPI: enrollSurface, apiData: enrollSurfaceData, isLoading: isLoadingEnrollSurface, serverError: serverErrorEnrollSurface } = useFetch({ scope: 'AdminUser' });

    const [messages, setMessages] = useState<NotificationMessage[]>([]);

    const config = featureConfigData?.[0];

    const hasValidCredentials = useMemo(() => 
        !!(config?.SurfaceWarrantyAppClientId && 
           config?.SurfaceWarrantyAppClientSecret && 
           config?.SurfaceWarrantySubscriptionKey),
        [config?.SurfaceWarrantyAppClientId, config?.SurfaceWarrantyAppClientSecret, config?.SurfaceWarrantySubscriptionKey]
    );
    const isTestPassed = config?.SurfaceStatusMessage === 'Tested Successfully';
    const enrollmentStatus = (config?.SurfaceWarrantyEnrollmentStatus || 'Not Enrolled') as 'Not Enrolled' | 'Pending' | 'Enrolled';

    const getTestIcon = () => {
        if (isTestPassed) return <CheckmarkCircleRegular style={{ color: 'green' }} />;
        if (config?.SurfaceStatusMessage === 'Test Failed') return <ErrorCircleRegular style={{ color: 'red' }} />;
        return <ErrorCircleRegular />;
    };

    const addMessage = (message: string, intent: MessageBarIntent) => {
        const newMessage = { intent, id: counterRef.current++, message };
        setMessages((s) => [newMessage, ...s]);
    };

    const dismissMessage = (messageId: number) =>
        setMessages((s) => s.filter((entry) => entry.id !== messageId));

    // Fetch configuration from API on component mount
    useEffect(() => {
        const fetchConfigAsync = async () => {
            await fetchConfig({ url: `admin/getfeatureconfig?featureName=${featureName}`, method: 'GET' });
        };
        fetchConfigAsync();
    }, [signInState.currentCustomerID]);

    useEffect(() => {
        if (!isLoading && !serverError && apiData) {
            console.log('[ModuleConfig-SurfaceWarranty] apiData', apiData);
            if (apiData && Array.isArray(apiData.result) && apiData.result.length > 0) {
                const featureConfigDataResult = (apiData as unknown as { result: SurfaceWarrantyFeatureConfigData[] }).result;
                setFeatureConfigData(featureConfigDataResult);

                const surfaceWarrantyConfigItemsResult: ModuleConfigTableProps[] = featureConfigDataResult.flatMap(item => ([
                    { key: 'SurfaceWarrantyAppClientId', displayName: 'Application Id', value: item.SurfaceWarrantyAppClientId || '' },
                    { key: 'SurfaceWarrantyAppClientSecret', displayName: 'Application Secret', value: item.SurfaceWarrantyAppClientSecret || '', secure: true },
                    { key: 'SurfaceWarrantySubscriptionKey', displayName: 'Surface Warranty API Key', value: item.SurfaceWarrantySubscriptionKey || '', secure: true },
                ]));
                setSurfaceWarrantyConfigItems(surfaceWarrantyConfigItemsResult);
            } else {
                // No data in DB - initialize with defaults
                console.log('[ModuleConfig-SurfaceWarranty] apiData is empty, using defaults');
                const defaultFeatureConfigData: SurfaceWarrantyFeatureConfigData[] = [{
                    AppFeatureID: 1,
                    SurfaceWarrantyEnabled: false,
                    SurfaceWarrantyAppClientId: '',
                    SurfaceWarrantyAppClientSecret: '',
                    SurfaceWarrantySubscriptionKey: '',
                    SyncSchedule7: '',
                    SurfaceStatus: 'Not Configured',
                    SurfaceStatusMessage: '',
                    SurfaceWarrantyLastSyncEnd: null,
                    SurfaceWarrantyLastSyncDuration: null,
                    SurfaceWarrantyEnrollmentStatus: 'Not Enrolled',
                    SurfaceWarrantyEnrollmentRequestDate: null
                }];
                setFeatureConfigData(defaultFeatureConfigData);

                const defaultConfigItems: ModuleConfigTableProps[] = [
                    { key: 'SurfaceWarrantyAppClientId', displayName: 'Application Id', value: '' },
                    { key: 'SurfaceWarrantyAppClientSecret', displayName: 'Application Secret', value: '', secure: true },
                    { key: 'SurfaceWarrantySubscriptionKey', displayName: 'Surface Warranty API Key', value: '', secure: true },
                ];
                setSurfaceWarrantyConfigItems(defaultConfigItems);
            }
        }
    }, [isLoading, serverError, apiData]);

    // Update configuration in DB and local state
    const updateConfiguration = async (key: string, value: string | number | boolean | Date) => {
        console.log('[ModuleConfig-SurfaceWarranty] Updating configuration:', key, value);
        const encodedValue = encodeURIComponent(value.toString());
        await updateDatabase({ url: `admin/updatefeatureconfig?featureName=${featureName}&${key}=${encodedValue}`, method: 'PUT' });

        // Optimistically update local state
        setFeatureConfigData((prev) => {
            if (!prev) return prev;
            return prev.map(item => ({ ...item, [key]: value }));
        });
        setSurfaceWarrantyConfigItems((prev) => {
            if (!prev) return prev;
            return prev.map(item =>
                item.key === key ? { ...item, value: value as string } : item
            );
        });
    };

    // Handle enable/disable toggle
    const handleConnectorToggle = async (enabled: boolean) => {
        console.log('[ModuleConfig-SurfaceWarranty] Toggling connector:', enabled);
        
        // Update local state optimistically
        setFeatureConfigData((prev) => {
            if (!prev) return prev;
            return prev.map(item => ({ ...item, SurfaceWarrantyEnabled: enabled }));
        });

        // Call enableDataSync endpoint
        await enableDataSync({ 
            url: 'admin/enabledatasync', 
            method: 'POST', 
            body: { SurfaceWarrantyEnabled: enabled } 
        });

        // Also update the feature config in DB
        await updateDatabase({ 
            url: `admin/updatefeatureconfig?featureName=${featureName}&SurfaceWarrantyEnabled=${enabled}`, 
            method: 'PUT' 
        });
    };

    // Test configuration by calling backend endpoint
    const testConfiguration = async () => {
        console.log('[ModuleConfig-SurfaceWarranty] Testing configuration');
        setMessages([]);
        setConnectorTestError(false);

        if (!config?.SurfaceWarrantyAppClientId || !isValidGuid(config.SurfaceWarrantyAppClientId)) {
            addMessage('The application id is not valid.', 'error');
            return;
        }

        await testConfig({ url: `admin/testfeatureconfig?featureName=${featureName}&connectorName=${connectorName}`, method: 'GET' });
    };

    // Handle test configuration response
    useEffect(() => {
        if (testConfigData) {
            console.log('[ModuleConfig-SurfaceWarranty] testResult:', testConfigData);
            if (testConfigData?.result === true) {
                console.log('[ModuleConfig-SurfaceWarranty] Configuration test successful');
                // Update local state directly to avoid race conditions
                setFeatureConfigData((prev) => {
                    if (!prev) return prev;
                    return prev.map(item => ({
                        ...item,
                        SurfaceStatus: 'Configured',
                        SurfaceStatusMessage: 'Tested Successfully'
                    }));
                });
                // Also update DB
                updateConfiguration('SurfaceStatus', 'Configured');
                updateConfiguration('SurfaceStatusMessage', 'Tested Successfully');
                setConnectorTestError(false);
                addMessage('Configuration test successful!', 'success');
            } else {
                console.log('[ModuleConfig-SurfaceWarranty] Configuration test failed');
                // Update local state directly to avoid race conditions
                setFeatureConfigData((prev) => {
                    if (!prev) return prev;
                    return prev.map(item => ({
                        ...item,
                        SurfaceStatus: 'Not Configured',
                        SurfaceStatusMessage: 'Test Failed',
                        SurfaceWarrantyEnabled: false
                    }));
                });
                // Also update DB
                updateConfiguration('SurfaceStatus', 'Not Configured');
                updateConfiguration('SurfaceStatusMessage', 'Test Failed');
                updateConfiguration('SurfaceWarrantyEnabled', false);
                setConnectorTestError(true);
                const errorMessage = testConfigData?.error || 'Configuration test failed. Please check your credentials.';
                addMessage(errorMessage.toString(), 'error');
            }
        }
    }, [testConfigData]);

    // Handle test configuration server error
    useEffect(() => {
        if (serverErrorTestConfig) {
            console.log('[ModuleConfig-SurfaceWarranty] Test config server error:', serverErrorTestConfig);
            // Update local state to reflect failure
            setFeatureConfigData((prev) => {
                if (!prev) return prev;
                return prev.map(item => ({
                    ...item,
                    SurfaceStatus: 'Not Configured',
                    SurfaceStatusMessage: 'Test Failed'
                }));
            });
            // Also update DB
            updateConfiguration('SurfaceStatus', 'Not Configured');
            updateConfiguration('SurfaceStatusMessage', 'Test Failed');
            updateConfiguration('SurfaceWarrantyEnabled', false);
            setConnectorTestError(true);
            addMessage(serverErrorTestConfig.message || 'Server error during configuration test', 'error');
        }
    }, [serverErrorTestConfig]);

    // Enrollment handler - calls backend to enroll tenant for Surface warranty
    const handleEnrollment = useCallback(async () => {
        console.log('[ModuleConfig-SurfaceWarranty] Enrolling tenant');
        setMessages([]);
        
        if (!config?.SurfaceWarrantyAppClientId || 
            !config?.SurfaceWarrantyAppClientSecret || 
            !config?.SurfaceWarrantySubscriptionKey) {
            addMessage('All credentials are required for enrollment', 'error');
            return;
        }

        await enrollSurface({
            url: `admin/enrollsurfacewarranty?featureName=${featureName}`,
            method: 'PUT',
            body: {
                SurfaceWarrantyAppClientId: config?.SurfaceWarrantyAppClientId,
                SurfaceWarrantyAppClientSecret: config?.SurfaceWarrantyAppClientSecret,
                SurfaceWarrantySubscriptionKey: config?.SurfaceWarrantySubscriptionKey
            }
        });
    }, [config?.SurfaceWarrantyAppClientId, config?.SurfaceWarrantyAppClientSecret, config?.SurfaceWarrantySubscriptionKey, enrollSurface]);

    // Handle enrollment response
    useEffect(() => {
        if (enrollSurfaceData) {
            console.log('[ModuleConfig-SurfaceWarranty] Enrollment result:', enrollSurfaceData);
            if (enrollSurfaceData.result) {
                const newStatus = enrollSurfaceData.result as 'Enrolled' | 'Pending';
                setFeatureConfigData((prev) => {
                    if (!prev) return prev;
                    return prev.map(item => ({ ...item, SurfaceWarrantyEnrollmentStatus: newStatus }));
                });
                
                if (newStatus === 'Enrolled') {
                    addMessage('Enrollment successful! Your tenant is now enrolled for Surface warranty data.', 'success');
                } else if (newStatus === 'Pending') {
                    addMessage('Enrollment submitted. Please allow up to 5 business days for the request to be processed.', 'warning');
                }
            } else if (enrollSurfaceData.error) {
                addMessage(`Enrollment failed: ${enrollSurfaceData.error}`, 'error');
            }
        }
    }, [enrollSurfaceData]);

    // Handle enrollment server error
    useEffect(() => {
        if (serverErrorEnrollSurface) {
            console.log('[ModuleConfig-SurfaceWarranty] Enrollment server error:', serverErrorEnrollSurface);
            addMessage(serverErrorEnrollSurface.message || 'Server error during enrollment', 'error');
        }
    }, [serverErrorEnrollSurface]);


    const enrollmentStatusIndicator = useMemo(() => {
        switch (enrollmentStatus) {
            case 'Enrolled':
                return (
                    <div className={styles.enrollmentStatus}>
                        <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>Enrolled</Badge>
                        <Text>Your tenant is enrolled for Surface warranty data.</Text>
                    </div>
                );
            case 'Pending': {
                const enrollmentRequestDate = config?.SurfaceWarrantyEnrollmentRequestDate;
                const isWithin5Days = enrollmentRequestDate 
                    ? (new Date().getTime() - new Date(enrollmentRequestDate).getTime()) <= (5 * 24 * 60 * 60 * 1000)
                    : true;

                return (
                    <div className={styles.enrollmentSection}>
                        <div className={styles.enrollmentStatus}>
                            <Badge appearance="filled" color="warning" icon={<ClockRegular />}>Pending</Badge>
                            <Text>Enrollment is pending.</Text>
                        </div>
                        {isWithin5Days && (
                            <MessageBar intent="warning">
                                <MessageBarBody>
                                    <MessageBarTitle>Please wait</MessageBarTitle>
                                    It may take up to 5 business days for the enrollment to become active.
                                </MessageBarBody>
                            </MessageBar>
                        )}
                    </div>
                );
            }
            default: // 'Not Enrolled'
                return (
                    <div className={styles.enrollmentSection}>
                        <div className={styles.enrollmentStatus}>
                            <Badge appearance="filled" color="danger" icon={<ErrorCircleRegular />}>Not Enrolled</Badge>
                            <Text>Your tenant is not enrolled for Surface warranty data.</Text>
                        </div>
                        <Button 
                            appearance="primary" 
                            onClick={handleEnrollment}
                            disabled={isLoadingEnrollSurface || !(hasValidCredentials && isTestPassed)}
                        >
                            {isLoadingEnrollSurface ? 'Enrolling...' : 'Enroll Now'}
                        </Button>
                    </div>
                );
        }
    }, [enrollmentStatus, config?.SurfaceWarrantyEnrollmentRequestDate, styles, handleEnrollment, isLoadingEnrollSurface, hasValidCredentials, isTestPassed]);

    return (
        <div className="SurfaceWarrantyConfig" style={{ margin: '20px', maxWidth: '1200px' }}>
            {isLoading && !serverError && !apiData &&
                <Card>
                    <Subtitle1>Surface Warranty Connector Setup</Subtitle1>
                    <FeatureSkeleton />
                </Card>
            }
            {serverError &&
                <Card>
                    <Subtitle1>Surface Warranty Connector Setup</Subtitle1>
                    <Text>Server Error: {serverError.message || String(serverError)}</Text>
                </Card>
            }
            {!isLoading && !serverError && featureConfigData && surfaceWarrantyConfigItems &&
                <Card>
                    <Subtitle1>Surface Warranty Connector Setup</Subtitle1>
                    <Text>Contact your Surface Account Manager for your Warranty API Keys.</Text>
                    <Text>More information can be found here:</Text>
                    <ul>
                        <li>
                            <AdminLink href="https://learn.workspacedna.com/knowledge-base/microsoft-surface-warranty-report" target="_blank">
                                Surface Management Portal Documentation
                            </AdminLink>
                        </li>
                    </ul>

                    <Switch
                        key="SurfaceWarrantyEnabled"
                        label={"Connector " + (config?.SurfaceWarrantyEnabled ? "enabled" : "disabled")}
                        checked={config?.SurfaceWarrantyEnabled}
                        disabled={isLoadingEnableDataSync || !isTestPassed || !tenantConfigured}
                        onChange={(e) => handleConnectorToggle(e.target.checked)}
                    />

                    <Text>Connector Status: {config?.SurfaceStatus || 'Not Configured'}</Text>
                    <Text>Connector Status Message: {config?.SurfaceStatusMessage || 'N/A'}</Text>
                    <Text>Connector Last Sync End Date: {config?.SurfaceWarrantyLastSyncEnd ? new Date(config.SurfaceWarrantyLastSyncEnd).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</Text>
                    <Text>Connector Last Sync Duration: {config?.SurfaceWarrantyLastSyncDuration == null
                        ? 'N/A'
                        : config?.SurfaceWarrantyLastSyncDuration === 0
                            ? '< 1 Min'
                            : `${config?.SurfaceWarrantyLastSyncDuration} mins`
                    }</Text>

                    <ModuleConfigTable<ModuleConfigTableProps[]>
                        data={surfaceWarrantyConfigItems}
                        appFeature={featureName}
                        onUpdate={updateConfiguration}
                    />

                    {/* Enrollment Status Section */}
                    {enrollmentStatusIndicator}

                    <div style={{ width: '50%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                        <Button
                            style={{ width: '100%' }}
                            onClick={testConfiguration}
                            disabled={isLoadingTestConfig || !hasValidCredentials}
                            icon={getTestIcon()}
                        >
                            {isLoadingTestConfig ? 'Testing...' : 'Test Configuration'}
                        </Button>
                    </div>

                    {/* Messages section */}
                    {messages.length > 0 && (
                        <MessageBarGroup className={styles.messageBarGroup}>
                            {messages.map(({ intent, id, message }) => (
                                <MessageBar key={`${intent}-${id}`} intent={intent}>
                                    <MessageBarBody>
                                        {connectorTestError && <MessageBarTitle>Testing Error:</MessageBarTitle>}
                                        {message}
                                    </MessageBarBody>
                                    <MessageBarActions
                                        containerAction={
                                            <Button
                                                onClick={() => dismissMessage(id)}
                                                aria-label="dismiss"
                                                appearance="transparent"
                                                icon={<DismissRegular />}
                                            />
                                        }
                                    />
                                </MessageBar>
                            ))}
                        </MessageBarGroup>
                    )}
                </Card>
            }
        </div>
    );
};

export default ModuleSurfaceWarranty;

